import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import sensorsApi from '../api/sensorsApi';

import './NumberInput.css';
import { resolveValueColorFromStops } from './liquidShared';

const NumberInput = ({
  title,
  transparentBackground,
  min,
  max,
  step,
  value,
  unit,
  showUnit = false,
  showMinMax = false,
  loopValues,
  color,
  buttonBgColor,
  buttonHoverBgColor,
  valueColor,
  valueHoverColor,
  changeColorBasedOnValue = false,
  colorGrading = false,
  colors = [],
  datastreamColor,
  draggable,
  pin,
  did,
  tid,
  contentAlignment,
  visualStyle = 'glass',
}) => {
  const containerRef = useRef(null);

  const resolveControlColor = (v) => {
    const s = String(v || '').trim();
    return s ? s : null;
  };

  const withAlpha = (hex, alpha = 0.15) => {
    const h = String(hex || '').trim();
    if (!h) return null;
    const m = h.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!m) return null;
    let raw = m[1];
    if (raw.length === 3) raw = raw.split('').map(ch => ch + ch).join('');
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    const a = Math.max(0, Math.min(1, Number(alpha)));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const resolvedControlColor = useMemo(() => {
    const rawColor = resolveControlColor(color);
    const normalizedColor = rawColor ? rawColor.toLowerCase() : '';
    if (normalizedColor === '#c52184') return '#3ccfe6';
    return rawColor || resolveControlColor(datastreamColor) || null;
  }, [color, datastreamColor]);

    const resolvedBtnTextColor = useMemo(() => {
    // Text color for + / - follows widget `color` (or datastream color)
    return resolvedControlColor || null;
  }, [resolvedControlColor]);

  const resolvedBtnBgColor = useMemo(() => {
    // Keep the CSS Figma glass gradient unless a custom button background is explicitly provided.
    return resolveControlColor(buttonBgColor) || null;
  }, [buttonBgColor]);

  const resolvedBtnHoverBgColor = useMemo(() => {
    // Same rule for hover: preserve the CSS effect by default.
    return resolveControlColor(buttonHoverBgColor) || null;
  }, [buttonHoverBgColor]);

  const resolvedValueColor = useMemo(() => {
    return resolveControlColor(valueColor) || '#ffffff';
  }, [valueColor]);

  const resolvedValueHoverColor = useMemo(() => {
    return resolveControlColor(valueHoverColor) || null;
  }, [valueHoverColor]);

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const toNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const hasMin = useMemo(() => Number.isFinite(Number(min)), [min]);
  const hasMax = useMemo(() => Number.isFinite(Number(max)), [max]);

  const numericMin = useMemo(() => (hasMin ? Number(min) : -Infinity), [hasMin, min]);

  const numericMax = useMemo(() => {
    if (!hasMax) return Infinity;
    const mx = Number(max);
    if (hasMin) {
      const mn = Number(min);
      return mx >= mn ? mx : mn;
    }
    return mx;
  }, [hasMax, max, hasMin, min]);

  const numericStep = useMemo(() => {
    const s = toNumber(step, 1);
    return Number.isFinite(s) && s > 0 ? s : 1;
  }, [step]);

  const baseForValue = useMemo(() => (hasMin ? Number(min) : 0), [hasMin, min]);

  const [numberValue, setNumberValue] = useState(String(toNumber(value, baseForValue)));
  const [edit, setEdit] = useState(false);
  const [cursorState, setCursorState] = useState('idle');

  const lastCommittedRef = useRef(String(toNumber(value, baseForValue)));
  const pendingStreamValueRef = useRef(null);
  const pendingControlValueRef = useRef(null);
  const pendingControlTimeoutRef = useRef(null);
  const lastExternalValueRef = useRef(String(toNumber(value, baseForValue)));

  const clearPendingControl = useCallback(() => {
    if (pendingControlTimeoutRef.current) {
      clearTimeout(pendingControlTimeoutRef.current);
      pendingControlTimeoutRef.current = null;
    }
    pendingControlValueRef.current = null;
  }, []);

  const valuesMatch = useCallback((left, right) => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
      return Math.abs(leftNumber - rightNumber) <= Math.max(1e-6, numericStep / 1000);
    }
    return String(left) === String(right);
  }, [numericStep]);

  useEffect(() => {
    const v = String(toNumber(value, baseForValue));
    lastExternalValueRef.current = v;

    if (edit) {
      pendingStreamValueRef.current = v;
      return;
    }

    if (pendingControlValueRef.current !== null) {
      if (!valuesMatch(v, pendingControlValueRef.current)) {
        return;
      }
      clearPendingControl();
    }

    setNumberValue(v);
    lastCommittedRef.current = v;
    pendingStreamValueRef.current = null;
  }, [value, baseForValue, edit, clearPendingControl, valuesMatch]);

  useEffect(() => () => {
    if (pendingControlTimeoutRef.current) {
      clearTimeout(pendingControlTimeoutRef.current);
    }
  }, []);

  const activeNumericValue = useMemo(() => {
    const local = Number(numberValue);
    if (Number.isFinite(local)) return local;

    const incoming = Number(value);
    if (Number.isFinite(incoming)) return incoming;

    return null;
  }, [numberValue, value]);

  const dynamicValueColor = useMemo(() => {
    if (activeNumericValue == null || !Number.isFinite(activeNumericValue)) {
      return null;
    }

    return resolveValueColorFromStops({
      value: clamp(activeNumericValue, numericMin, numericMax),
      baseColor: null,
      stops: colors,
      enabled: changeColorBasedOnValue,
      grading: colorGrading,
    });
  }, [
    changeColorBasedOnValue,
    colors,
    activeNumericValue,
    numericMin,
    numericMax,
    colorGrading,
  ]);

  const activeValueColor = useMemo(
    () => dynamicValueColor || resolvedValueColor,
    [dynamicValueColor, resolvedValueColor]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    if (resolvedControlColor) {
      containerRef.current.style.setProperty("--numberinput-control-color", resolvedControlColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-control-color");
    }

    if (resolvedBtnTextColor) {
      containerRef.current.style.setProperty("--numberinput-button-color", resolvedBtnTextColor);
      containerRef.current.style.setProperty("--numberinput-button-border-color", resolvedBtnTextColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-color");
      containerRef.current.style.removeProperty("--numberinput-button-border-color");
    }

    if (resolvedBtnBgColor) {
      containerRef.current.style.setProperty("--numberinput-button-bg", resolvedBtnBgColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-bg");
    }

    if (resolvedBtnHoverBgColor) {
      containerRef.current.style.setProperty("--numberinput-button-hover-bg", resolvedBtnHoverBgColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-button-hover-bg");
    }

    if (activeValueColor) {
      containerRef.current.style.setProperty("--numberinput-value-color", activeValueColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-value-color");
    }

    if (resolvedValueHoverColor) {
      containerRef.current.style.setProperty("--numberinput-value-hover-color", resolvedValueHoverColor);
    } else {
      containerRef.current.style.removeProperty("--numberinput-value-hover-color");
    }
  }, [resolvedControlColor, resolvedBtnTextColor, resolvedBtnBgColor, resolvedBtnHoverBgColor, activeValueColor, resolvedValueHoverColor]);

  const pushToDevice = async (nextStr) => {
    if (!did) return true;
    const body = JSON.stringify({ device: did, template: tid, data: { [pin]: nextStr } });
    try {
      await sensorsApi.control(body);
      return true;
    } catch (err) {
      console.log(err?.message || String(err));
      return false;
    }
  };

  const commitValue = (rawValue) => {
    const parsed = parseFloat(String(rawValue));
    if (!Number.isFinite(parsed)) return;

    const clamped = clamp(parsed, numericMin, numericMax);
    const rounded = Math.round(clamped * 1_000_000) / 1_000_000;
    const nextStr = String(rounded);

    setNumberValue(nextStr);
    lastCommittedRef.current = nextStr;

    if (did) {
      clearPendingControl();
      pendingControlValueRef.current = nextStr;
      pendingControlTimeoutRef.current = setTimeout(() => {
        pendingControlValueRef.current = null;
        pendingControlTimeoutRef.current = null;
        setNumberValue(lastExternalValueRef.current);
        lastCommittedRef.current = lastExternalValueRef.current;
      }, 2500);
    }

    pushToDevice(nextStr).then((succeeded) => {
      if (
        succeeded ||
        pendingControlValueRef.current === null ||
        !valuesMatch(pendingControlValueRef.current, nextStr)
      ) return;
      clearPendingControl();
      setNumberValue(lastExternalValueRef.current);
      lastCommittedRef.current = lastExternalValueRef.current;
    });
  };

  const handleTypedChange = (raw) => {
    const str = String(raw);

    if (/^-?\d*\.?\d*$/.test(str)) {
      setNumberValue(str);
    }
  };

  const finalizeEdit = () => {
    const str = String(numberValue);

    if (
      str === '' ||
      str === '-' ||
      str === '.' ||
      str === '-.' ||
      !Number.isFinite(parseFloat(str))
    ) {
      setNumberValue(lastCommittedRef.current);
    } else {
      commitValue(str);
    }

    setEdit(false);
  };

  const startEdit = () => {
    setNumberValue(lastCommittedRef.current);
    setEdit(true);
  };

  const canLoop = loopValues && Number.isFinite(numericMin) && Number.isFinite(numericMax);

  const dec = () => {
    const current = toNumber(numberValue, baseForValue);
    let next = current - numericStep;

    if (canLoop) {
      if (next < numericMin) next = numericMax;
    } else if (Number.isFinite(numericMin)) {
      if (next < numericMin) next = numericMin;
    }

    next = Math.round(next * 1_000_000) / 1_000_000;
    commitValue(next);
  };

  const inc = () => {
    const current = toNumber(numberValue, baseForValue);
    let next = current + numericStep;

    if (canLoop) {
      if (next > numericMax) next = numericMin;
    } else if (Number.isFinite(numericMax)) {
      if (next > numericMax) next = numericMax;
    }

    next = Math.round(next * 1_000_000) / 1_000_000;
    commitValue(next);
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'number-input--grabbing' : 'number-input--grab')
    : 'number-input--auto';
  const normalizedContentAlignment = useMemo(() => {
    const nextAlignment = String(contentAlignment || 'auto').trim().toLowerCase();
    return ['auto', 'top', 'center', 'bottom', 'title-line'].includes(nextAlignment)
      ? nextAlignment
      : 'auto';
  }, [contentAlignment]);
  const hasUnit = unit != null && String(unit).trim() !== '';

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "number-input",
        cursorClass,
        `number-input--align-${normalizedContentAlignment}`,
        visualStyle === 'classic' ? 'number-input--classic' : 'number-input--glass',
        transparentBackground ? "number-input--transparent" : "",
      ].filter(Boolean).join(" ")}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `number;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <div className="number-input__title">
        {title}
      </div>

      <div className="number-input__row the-slider">
        <button
          className="number-input__btn"
          onClick={dec}
          onMouseDown={(e) => e.preventDefault()}
          title="Decrease"
          type="button"
        >
          <span className="number-input__stepIcon number-input__stepIcon--minus" aria-hidden="true">−</span>
        </button>

        {edit ? (
          <input
            value={numberValue}
            autoFocus
            className="widget-input number-input__editInput"
            inputMode="decimal"
            onBlur={finalizeEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finalizeEdit();
              if (e.key === 'Escape') {
                setNumberValue(lastCommittedRef.current);
                setEdit(false);
              }
            }}
            onChange={(e) => handleTypedChange(e.target.value)}
          />
        ) : (
          <div
            className="slider-value number-input__value"
            title={numberValue}
            onClick={startEdit}
          >
            <span className="number-input__valueText">
              {numberValue}
              {showUnit && hasUnit ? ` ${String(unit).trim()}` : ''}
            </span>
          </div>
        )}

        <button
          className="number-input__btn"
          onClick={inc}
          onMouseDown={(e) => e.preventDefault()}
          title="Increase"
          type="button"
        >
          <span className="number-input__stepIcon number-input__stepIcon--plus" aria-hidden="true">+</span>
        </button>
      </div>

      {showMinMax ? (
        <div className="number-input__limits">
          <span>{Number.isFinite(numericMin) ? `Min:${numericMin}` : ''}</span>
          <span>{Number.isFinite(numericMax) ? `Max:${numericMax}` : ''}</span>
        </div>
      ) : null}
    </div>
  );
};

export default NumberInput;
